import React from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, Trash2, Plus, Minus, Loader2, Truck } from "lucide-react";
import api from "../lib/api";
import { useSEO } from "../components/site/Reveal";
import Newsletter from "../components/site/Newsletter";
import { useCart, updateQty, removeItem, cartSubtotal, clearCart } from "../lib/cart";
import { useCurrency } from "../context/CurrencyContext";
import { useMember } from "../context/MemberAuthContext";
import CurrencySelector from "../components/site/CurrencySelector";
import PaystackButton from "../components/site/PaystackButton";
import { toast } from "sonner";

export default function Cart() {
  useSEO("Your Cart", "Review your ÀsÀNigHT Worldwide shop cart and checkout securely.");
  const cart = useCart();
  const [email, setEmail] = React.useState("");
  const { member } = useMember();
  React.useEffect(() => { if (member?.email) setEmail((prev) => prev || member.email); }, [member]);
  const [busy, setBusy] = React.useState("");
  const subtotal = cartSubtotal();
  const { format, isConverted, ratesUpdatedText } = useCurrency();
  const paypalRef = React.useRef(null);
  const [ppReady, setPpReady] = React.useState(false);

  // Load PayPal SDK
  React.useEffect(() => {
    api.get("/paypal/config").then(({ data }) => {
      if (!data.configured || !data.client_id) return;
      if (window.paypal) { setPpReady(true); return; }
      const s = document.createElement("script");
      s.src = `https://www.paypal.com/sdk/js?client-id=${data.client_id}&currency=USD`;
      s.onload = () => setPpReady(true);
      document.body.appendChild(s);
    }).catch(() => {});
  }, []);

  React.useEffect(() => {
    if (!ppReady || !window.paypal || !paypalRef.current || cart.length === 0) return;
    paypalRef.current.innerHTML = "";
    window.paypal.Buttons({
      style: { color: "gold", shape: "pill", label: "pay", height: 45 },
      createOrder: async () => {
        const { data } = await api.post("/shop/checkout", { items: cart.map((c) => ({ id: c.id, qty: c.qty, variant: c.variant })), origin_url: window.location.origin, email, method: "paypal" });
        return data.paypal_order_id;
      },
      onApprove: async (data) => {
        const { data: res } = await api.post(`/shop/paypal-capture/${data.orderID}`);
        if (res.payment_status === "paid") { clearCart(); window.location.href = "/shop/success?paid=paypal"; }
      },
    }).render(paypalRef.current);
  }, [ppReady, cart, email]);

  const stripeCheckout = async () => {
    setBusy("stripe");
    try {
      const { data } = await api.post("/shop/checkout", { items: cart.map((c) => ({ id: c.id, qty: c.qty, variant: c.variant })), origin_url: window.location.origin, email, method: "stripe" });
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch { setBusy(""); }
  };

  return (
    <div className="min-h-screen">
      <div className="max-w-4xl mx-auto px-6 pt-28 pb-20">
        <h1 className="font-serif text-4xl lg:text-5xl text-cream mb-8" data-testid="cart-title">Your Cart</h1>
        {cart.length === 0 ? (
          <div className="text-center border border-dashed border-cream/20 rounded-2xl py-16" data-testid="cart-empty">
            <ShoppingCart className="w-10 h-10 text-cream/40 mx-auto mb-4" />
            <p className="text-cream/70 mb-6">Your cart is empty.</p>
            <Link to="/shop" className="inline-flex bg-gold text-night font-semibold px-6 py-3 rounded-full">Browse the Shop</Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-4">
              {cart.map((it, idx) => (
                <div key={idx} data-testid={`cart-item-${idx}`} className="flex gap-4 bg-night-surface border border-cream/10 rounded-xl p-4">
                  {it.image && <img src={it.image} alt={it.name} className="w-20 h-20 object-cover rounded-lg" />}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-serif text-lg text-cream">{it.name}</h3>
                    {it.variant && Object.keys(it.variant).length > 0 && (
                      <p className="text-xs text-cream/50">{Object.entries(it.variant).map(([k, v]) => `${k}: ${v}`).join(" · ")}</p>
                    )}
                    <p className="text-gold mt-1">{format(it.price)}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <div className="flex items-center border border-cream/20 rounded-full">
                        <button onClick={() => updateQty(idx, it.qty - 1)} className="grid place-items-center w-8 h-8 text-cream"><Minus className="w-3.5 h-3.5" /></button>
                        <span className="w-7 text-center text-cream text-sm">{it.qty}</span>
                        <button onClick={() => updateQty(idx, it.qty + 1)} className="grid place-items-center w-8 h-8 text-cream"><Plus className="w-3.5 h-3.5" /></button>
                      </div>
                      <button onClick={() => removeItem(idx)} data-testid={`remove-item-${idx}`} className="text-cream/50 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                  <p className="font-serif text-cream">{format(it.price * it.qty)}</p>
                </div>
              ))}
            </div>
            <div className="bg-night-surface border border-cream/10 rounded-2xl p-6 h-fit">
              <h3 className="font-serif text-xl text-cream mb-4">Summary</h3>
              <div className="flex justify-end mb-3"><CurrencySelector variant="dark" /></div>
              <div className="flex justify-between text-cream/80 mb-2"><span>Subtotal</span><span data-testid="cart-subtotal">{format(subtotal)}</span></div>
              {isConverted && <p className="text-xs text-gold/80 mb-2">You'll be charged ${subtotal.toFixed(2)} USD — other currencies are shown for reference at today's rate.</p>}
              {isConverted && ratesUpdatedText && <p className="text-[11px] text-cream/40 mb-2 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" /> {ratesUpdatedText}</p>}
              <p className="text-xs text-cream/50 flex items-center gap-1.5 mb-4"><Truck className="w-3.5 h-3.5" /> Shipping & tax calculated at checkout</p>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email for receipt" data-testid="cart-email-input" className="w-full rounded-lg bg-night border border-cream/20 px-4 py-2.5 text-cream placeholder:text-cream/40 mb-3 focus:outline-none focus:border-gold" />
              <button onClick={stripeCheckout} disabled={busy === "stripe"} data-testid="stripe-checkout-button" className="w-full inline-flex items-center justify-center gap-2 bg-gold text-night font-semibold py-3 rounded-full hover:bg-gold-light transition-colors disabled:opacity-60">
                {busy === "stripe" ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : "Pay with Card (Stripe)"}
              </button>
              <div className="text-center text-xs text-cream/40 my-3">or</div>
              <div ref={paypalRef} data-testid="paypal-buttons" />
              <PaystackButton
                variant="dark"
                disabled={cart.length === 0}
                initialize={async (cur) => {
                  if (!email) { toast.error("Please enter your email first."); throw new Error("email required"); }
                  const { data } = await api.post("/shop/checkout", { items: cart.map((c) => ({ id: c.id, qty: c.qty, variant: c.variant })), origin_url: window.location.origin, email, method: "paystack", currency: cur });
                  return data.authorization_url;
                }}
              />
            </div>
          </div>
        )}
      </div>
      <Newsletter />
    </div>
  );
}
